// Compatibility layer for migrating from mdbreact to react-bootstrap
import React from 'react';
import {
    Button as BSButton,
    Card as BSCard,
    Modal as BSModal,
    Form,
    Alert as BSAlert,
    Badge as BSBadge,
    Spinner,
    OverlayTrigger,
    Popover as BSPopover,
    Tooltip as BSTooltip,
    Toast
} from 'react-bootstrap';

// Button wrapper
export const MDBBtn = ({color, size, children, className = '', ...props}) => {
    const variant = color === 'unique' ? 'primary' : color === 'elegant' ? 'dark' : color === 'grey' ? 'secondary' : color || 'primary';
    return <BSButton variant={variant} size={size} className={className} {...props}>{children}</BSButton>;
};

// Card components
export const MDBCard = ({children, className = '', color, ...props}) => {
    const bgClass = color ? `bg-${color}` : '';
    return <BSCard className={`${bgClass} ${className}`} {...props}>{children}</BSCard>;
};

export const MDBCardBody = BSCard.Body;
export const MDBCardHeader = BSCard.Header;
export const MDBCardText = BSCard.Text;

// Layout components
export const MDBContainer = ({children, className = '', fluid, ...props}) => {
    return <div className={`container${fluid ? '-fluid' : ''} ${className}`} {...props}>{children}</div>;
};

export const MDBRow = ({children, className = '', center, middle, around, between, end, start, ...props}) => {
    // Filter out MDB-specific props that shouldn't be passed to DOM
    const classes = [className];
    if (center) classes.push('justify-content-center');
    if (middle) classes.push('align-items-center');
    if (around) classes.push('justify-content-around');
    if (between) classes.push('justify-content-between');
    if (end) classes.push('justify-content-end');
    if (start) classes.push('justify-content-start');
    return <div className={`row ${classes.join(' ')}`} {...props}>{children}</div>;
};

export const MDBCol = ({children, className = '', ...props}) => {
    return <div className={`col ${className}`} {...props}>{children}</div>;
};

export const MDBBox = ({children, className = '', ...props}) => {
    return <div className={className} {...props}>{children}</div>;
};

// Modal components
export const MDBModal = ({isOpen, toggle, children, ...props}) => {
    return <BSModal show={isOpen} onHide={toggle} {...props}>{children}</BSModal>;
};

export const MDBModalHeader = ({children, toggle, ...props}) => {
    return <BSModal.Header closeButton={!!toggle} {...props}>{children}</BSModal.Header>;
};

export const MDBModalBody = BSModal.Body;
export const MDBModalFooter = BSModal.Footer;

// Form components
export const MDBInput = ({label, name, value, onChange, type = 'text', children, group, ...props}) => {
    // Extract validation feedback from children if present
    const feedback = children?.props?.children;

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label>{label}</Form.Label>}
            <Form.Control
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                {...props}
            />
            {feedback && (
                <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                    {feedback}
                </Form.Control.Feedback>
            )}
        </Form.Group>
    );
};

// Alert
export const MDBAlert = ({children, color, ...props}) => {
    return <BSAlert variant={color || 'info'} {...props}>{children}</BSAlert>;
};

// Badge
export const MDBBadge = ({children, color, ...props}) => {
    return <BSBadge bg={color || 'primary'} {...props}>{children}</BSBadge>;
};

// Icon - using basic span, you'll need fontawesome
export const MDBIcon = ({icon, className = '', ...props}) => {
    return <i className={`fa fa-${icon} ${className}`} {...props} />;
};

// Link
export const MDBLink = ({children, to, href, ...props}) => {
    return <a href={to || href} {...props}>{children}</a>;
};

// View and Mask (simplified - these were for parallax/hero effects)
export const MDBView = ({children, className = '', ...props}) => {
    return <div className={`view ${className}`} {...props}>{children}</div>;
};

export const MDBMask = ({children, className = '', ...props}) => {
    return <div className={`mask ${className}`} {...props}>{children}</div>;
};

// Navbar components
export const MDBNavbar = ({children, className = '', color, dark, expand, ...props}) => {
    const classes = [className, 'navbar'];
    if (color) classes.push(`bg-${color}`);
    if (dark) classes.push('navbar-dark');
    if (expand) classes.push(`navbar-expand-${expand}`);
    return <nav className={classes.join(' ')} {...props}>{children}</nav>;
};

export const MDBNavbarBrand = ({children, href, to, ...props}) => {
    // If it has href/to, render as link, otherwise as span to avoid nested anchors
    if (href || to) {
        return <a className="navbar-brand" href={to || href} {...props}>{children}</a>;
    }
    return <span className="navbar-brand" {...props}>{children}</span>;
};

export const MDBNavbarNav = ({children, className = '', left, right, ...props}) => {
    const classes = [className, 'navbar-nav'];
    if (left) classes.push('me-auto');
    if (right) classes.push('ms-auto');
    return <ul className={classes.join(' ')} {...props}>{children}</ul>;
};

export const MDBNavItem = ({children, active, ...props}) => {
    const classes = ['nav-item'];
    if (active) classes.push('active');
    return <li className={classes.join(' ')} {...props}>{children}</li>;
};

export const MDBNavLink = ({children, to, href, ...props}) => {
    return <a className="nav-link" href={to || href} {...props}>{children}</a>;
};

export const MDBNavbarToggler = ({onClick, ...props}) => {
    return (
        <button className="navbar-toggler" type="button" onClick={onClick} {...props}>
            <span className="navbar-toggler-icon"></span>
        </button>
    );
};

export const MDBCollapse = ({isOpen, children, navbar, ...props}) => {
    return (
        <div className={`collapse ${navbar ? 'navbar-collapse' : ''} ${isOpen ? 'show' : ''}`} {...props}>
            {children}
        </div>
    );
};

// Footer
export const MDBFooter = ({children, className = '', ...props}) => {
    return <footer className={`footer ${className}`} {...props}>{children}</footer>;
};

// Notification/Toast
export const MDBNotification = ({show, onClose, title, message, icon, iconClassName, ...props}) => {
    return (
        <Toast show={show} onClose={onClose} {...props}>
            {title && (
                <Toast.Header>
                    {icon && <i className={`fa fa-${icon} ${iconClassName || ''} me-2`} />}
                    <strong className="me-auto">{title}</strong>
                </Toast.Header>
            )}
            <Toast.Body>{message}</Toast.Body>
        </Toast>
    );
};

// Popover
export const MDBPopover = ({placement, children, popover, ...props}) => {
    const popoverComponent = (
        <BSPopover {...props}>
            {popover}
        </BSPopover>
    );

    // Wrap children in a span to ensure single child for OverlayTrigger
    return (
        <OverlayTrigger placement={placement} overlay={popoverComponent}>
            <span>{children}</span>
        </OverlayTrigger>
    );
};

export const MDBPopoverBody = BSPopover.Body;

// Tooltip
export const MDBTooltip = ({placement, children, tooltipContent, ...props}) => {
    const tooltip = <BSTooltip {...props}>{tooltipContent}</BSTooltip>;

    // Wrap children in a span to ensure single child for OverlayTrigger
    return (
        <OverlayTrigger placement={placement || 'top'} overlay={tooltip}>
            <span>{children}</span>
        </OverlayTrigger>
    );
};
